<template>
  <v-card>
    <v-toolbar v-if="canAddUsers" :dark="$vuetify.theme.dark" color="primary">
      <v-row align-content="center">
        <v-col cols="10" class="text-left" />
        <v-col class="text-right" align-self="center">
          <btn-icon-tip
              id="addUser"
              icon="add"
              icon-color="secondaryiconbtn"
              :disabled="isLoading"
              :tip="$t('Добавить УП')"
              @click="openAddUserDialog" />
        </v-col>
      </v-row>
    </v-toolbar>
    <v-card-text>
      <paginated-data-table
          v-if="getOrganizationUsers"
          ref="paginatedTable"
          :headers="headers"
          :items="users"
          :loading="isLoading"
          item-key="userId"
          :total-count="totalCount"
          :items-per-page="itemsPerPage"
          :request-method="getOrganizationUsers">
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.userName="{ item }">
          <route-link-with-tip
              :text="item.userName"
              :tip="$t('Просмотр пользователя')"
              :to="{ path: `${urlRootPath}/user/${item.userId}` }" />
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.contactPerson="{ item }">
          {{ item.contactPerson }}
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.email="{ item }">
          {{ item.email }}
        </template>
        <!-- eslint-disable-next-line vue/valid-v-slot -->
        <template v-slot:item.isLocked="{ item }">
          <btn-icon-tip
              v-if="isAdmin || isOrganizer"
              :id="`lock-${item.userId}`"
              :icon="item.isLocked
                ? 'lock'
                : 'lock_open'"
              :icon-color="item.isLocked
                ? 'error'
                : 'success'"
              :tip="$t('Изменить статус. Текущий: {0}', [ item.isLocked
                ? $t('Заблокирован')
                : $t('Активный') ] )"
              @click="openStatusDialog(item.userId, item.isLocked)" />
          <icon-tip
              v-else
              :icon="item.isLocked
                ? 'lock'
                : 'lock_open'"
              :icon-color="item.isLocked
                ? 'error'
                : 'success'"
              :tip="$t('Текущий статус: {0}', [ item.isLocked
                ? $t('Заблокирован')
                : $t('Активный') ] )" />
        </template>
      </paginated-data-table>
    </v-card-text>
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        @close="closeDialog('confirmDialog')" />
    <add-user-dialog
        v-if="addUserDialog.show"
        :dialog-prop="addUserDialog"
        @close="closeDialog('addUserDialog')" />
  </v-card>
</template>

<script>
  import AddUserDialog from '@/components/pages/admin/Organizations/Summary/AddUserDialog.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import IconTip from '@/components/reused/ui/IconTip.vue'
  import RouteLinkWithTip from '@/components/reused/ui/RouteLinkWithTip.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import PaginatedDataTable from '@/components/reused/ui/PaginatedDataTable.vue'
  import { mapActions, mapState } from 'vuex'
  import { ORGANIZATION_MANAGEMENT, USER_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      AddUserDialog,
      IconTip,
      RouteLinkWithTip,
      ConfirmDialog,
      PaginatedDataTable
    },
    props: {
      organizationId: {
        type: String,
        required: true
      },
      canAddUsers: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isLoading: false,
        itemsPerPage: 10,
        confirmDialog: {
          show: false,
          additional: null,
          text: null,
          callback: null,
          warning: true
        },
        addUserDialog: {
          show: false,
          organizationId: null,
          callback: null
        }
      }
    },
    computed: {
      ...mapState(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        usersInfo: state => (state[ORGANIZATION_MANAGEMENT.STATE.USERS] || { })
      }),
      totalCount () {
        return this.usersInfo.totalCount || 0
      },
      users () {
        return this.usersInfo.items || []
      },
      headers () {
        return [
          { text: this.$t('Имя пользователя'), align: 'start', sortable: true, value: 'userName' },
          { text: this.$t('Контактное лицо'), align: 'start', sortable: false, value: 'contactPerson' },
          { text: this.$t('Email'), align: 'start', sortable: false, value: 'email' },
          { text: this.$t('Статус блокировки'), align: 'center', sortable: false, value: 'isLocked' }
        ]
      }
    },
    created () {
      this.clearStore()
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        getOrganizationUsersAction: ORGANIZATION_MANAGEMENT.ACTIONS.GET_ORGANIZATION_USERS,
        clearOrganizationUsersAction: ORGANIZATION_MANAGEMENT.ACTIONS.CLEAR_ORGANIZATION_USERS
      }),
      ...mapActions(USER_MANAGEMENT.NAMESPACE, {
        changeUserLockStatusAction: USER_MANAGEMENT.ACTIONS.CHANGE_USER_LOCK_STATUS
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearOrganizationUsersAction()
          ])
      },
      getOrganizationUsers: async function (params) {
        try {
          this.isLoading = true
          await this.getOrganizationUsersAction({
            organizationId: this.organizationId,
            params
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      openStatusDialog: function (userId, isLocked) {
        this.confirmDialog.additional = {
          isLocked: !isLocked,
          userId
        }
        const action = isLocked
          ? this.$t('разблокирован')
          : this.$t('заблокирован')
        this.confirmDialog.text = this.$t('Пользователь будет {0}. Продолжить?', [action])
        this.confirmDialog.callback = this.changeUserLockStatus
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      openAddUserDialog: function (userId, isLocked) {
        this.addUserDialog.organizationId = this.organizationId
        this.addUserDialog.callback = this.$refs.paginatedTable.getDataFromServer
        this.addUserDialog.show = true
      },
      changeUserLockStatus: async function (status) {
        this.isLoading = true
        try {
          await this.changeUserLockStatusAction(status)
          this.isLoading = false
          this.$refs.paginatedTable.getDataFromServer()
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
